import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquarePlus, LifeBuoy, Inbox } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Card } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { supportApi, SupportTicket, TICKET_CATEGORY_LABELS } from "@/lib/api/support";
import { TicketStatusBadge } from "@/components/support/TicketStatusBadge";

const PAGE_SIZE = 20;

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supportApi
      .listMine({ limit: PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setTickets(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getApiErrorMessage(err, "Failed to load tickets"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.subject.toLowerCase().includes(q) ||
        t.ticketNumber.toLowerCase().includes(q) ||
        TICKET_CATEGORY_LABELS[t.category].toLowerCase().includes(q)
    );
  }, [tickets, search]);

  const columns: DataTableColumn<SupportTicket>[] = [
    {
      id: "number",
      header: "Ticket",
      width: "w-32",
      cell: (t) => (
        <Link
          to={`/support/${t.id}`}
          className="font-mono text-sm font-medium text-primary hover:underline"
        >
          {t.ticketNumber}
        </Link>
      ),
    },
    {
      id: "subject",
      header: "Subject",
      cell: (t) => (
        <div className="min-w-0">
          <Link to={`/support/${t.id}`} className="font-medium text-slate-900 hover:underline">
            {t.subject}
          </Link>
          <div className="mt-0.5 text-xs text-slate-500">
            {TICKET_CATEGORY_LABELS[t.category]}
            {t.databaseName ? ` · ${t.databaseName}` : ""}
          </div>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      width: "w-40",
      cell: (t) => <TicketStatusBadge status={t.status} patronView />,
    },
    {
      id: "updated",
      header: "Last activity",
      width: "w-40",
      hideOnMobile: true,
      cell: (t) => (
        <span className="text-xs text-slate-500">{new Date(t.lastMessageAt).toLocaleString()}</span>
      ),
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Support tickets"
        description="Ask the library team anything — inquiries, technical support, or database access requests."
        breadcrumbItems={[{ label: "Support" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search your tickets…"
        actions={
          <Button onClick={() => navigate("/support/new")} className="gap-2">
            <MessageSquarePlus className="h-4 w-4" />
            New ticket
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          data={filtered}
          rowKey={(t) => t.id}
          loading={loading}
          emptyState={
            search ? (
              <EmptyState
                icon={<Inbox className="h-6 w-6 text-slate-400" />}
                title="No matches"
                description="Try a different search term."
              />
            ) : (
              <EmptyState
                icon={<LifeBuoy className="h-6 w-6 text-slate-400" />}
                title="No tickets yet"
                description="Open a ticket whenever you need help from the library team."
                action={
                  <Button onClick={() => navigate("/support/new")} className="gap-2">
                    <MessageSquarePlus className="h-4 w-4" />
                    Open your first ticket
                  </Button>
                }
              />
            )
          }
        />
      </Card>
    </PageContainer>
  );
}
